/*
 * Copyright (c) 2025 Cozy Intellect Inc.
 * File Path: apps/desktop/src/lib/layout.store.ts
 */

import { page } from '$app/stores';
import { signedInOrLoading } from '@repo/sharedlib/src/stores/auth.store';
import type { Readable } from 'svelte/store';
import { derived, readable, writable } from 'svelte/store';
import constants from '../utils/constants';

const MOBILE_BREAKPOINT = 640; // sm breakpoint in Tailwind
const isWeb = constants.PLATFORM === 'web';

/**
 * Window width store - tracks current window width for responsive behavior
 * Initialized on first subscription, cleaned up when no subscribers
 */
function createWindowWidthStore() {
  const { subscribe, set } = writable(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  let listenerAdded = false;

  return {
    subscribe: (run: (value: number) => void, invalidate?: () => void) => {
      // Add resize listener on first subscription
      if (typeof window !== 'undefined' && !listenerAdded) {
        const handleResize = () => set(window.innerWidth);
        window.addEventListener('resize', handleResize);
        listenerAdded = true;
      }

      return subscribe(run, invalidate);
    }
  };
}

export const windowWidth = createWindowWidthStore();

/**
 * Determines if the current view is mobile web (web platform with small screen)
 * Used to show mobile-optimized layouts on small web browsers
 */
export const isMobileWeb: Readable<boolean> = derived(windowWidth, ($windowWidth) => {
  return isWeb && $windowWidth < MOBILE_BREAKPOINT;
});

/**
 * Layout store for desktop app
 * Centralizes layout logic to ensure consistent spacing and behavior
 * across components, especially for macOS traffic light buttons
 */

/**
 * Determines if the sidebar grid should be hidden
 * Grid is hidden when:
 * - User is not signed in (or still loading auth)
 * - OR on the home page '/'
 * - OR in full-screen editor mode (e.g., drafts)
 */
export const hideGrid = derived<[Readable<boolean>, Readable<{ url: URL }>], boolean>(
  [signedInOrLoading, page],
  ([$signedInOrLoading, $page]) => {
    // Hide grid when: not signed in, on home page, OR on drafts page (full-screen editor)
    const result = !$signedInOrLoading || $page.url.pathname === '/';
    return result;
  }
);

/**
 * Determines if top bar elements need left padding to avoid macOS traffic lights
 * Padding needed when grid is hidden (same condition as hideGrid)
 * This ensures buttons don't overlap with window controls
 */
export const needsTrafficLightPadding = constants.IS_DESKTOP
  ? derived<[Readable<boolean>, Readable<{ url: URL }>], boolean>(
      [signedInOrLoading, page],
      ([$signedInOrLoading, $page]) => {
        return (
          !$signedInOrLoading ||
          $page.url.pathname === '/' ||
          $page.url.pathname.startsWith('/app/drafts') ||
          $page.url.pathname.startsWith('/app/drive/editor')
        );
      }
    )
  : readable(false);

export const fullScreenEditor = derived<[Readable<{ url: URL }>], boolean>([page], ([$page]) => {
  return $page.url.pathname.startsWith('/app/drafts') || $page.url.pathname.startsWith('/app/drive/editor');
});
